import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getPremiumStyled, RETURN_VALUE_ONLY } from 'enrollment/utils';
import '../index.css';
import { IncrementDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { selectPerPayPeriodText } from 'enrollment/services';

interface IncrementAmountProps {
  show: boolean;
  lastNonEoiVolume: number;
  maxValue: number;
  onAmountChange: (increment?: IncrementDto) => void;
  setCost: (cost: string) => void;
  increments: IncrementDto[];
  onButtonClick: (value: number) => void;
  setShowCoverageNumbers: React.Dispatch<React.SetStateAction<boolean>>;
  selectRef: React.RefObject<HTMLSelectElement>;
  hideEmptyCost: boolean;
}
export const IncrementAmounts = ({
  increments,
  show,
  lastNonEoiVolume,
  maxValue,
  onAmountChange,
  setCost,
  onButtonClick,
  setShowCoverageNumbers,
  selectRef,
  hideEmptyCost,
}: IncrementAmountProps) => {
  const numbersRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('pages.volume');
  const perPayText = useSelector(selectPerPayPeriodText);

  useEffect(() => {
    // Whenever the `show` prop changes to true, focus the `div`.
    if (show && numbersRef.current) {
      numbersRef.current.focus();
    }
  }, [show]);

  useEffect(() => {
    const keyListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const selectElement = selectRef.current?.lastElementChild
          ?.firstElementChild as HTMLSelectElement | null | undefined;
        if (selectElement) {
          selectElement.focus();
        }
        setShowCoverageNumbers(false);
      }
    };
    document.addEventListener('keydown', (e) => keyListener(e));

    return () => document.removeEventListener('keydown', (e) => keyListener(e));
  });

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Use data-volume attribute for reliable matching
    const button = event.currentTarget;
    const volumeValue = button.getAttribute('data-volume');
    const increment = increments.find(
      (inc) => String(inc.volume) === volumeValue
    );

    // Extracting the subscriber premium and formatting it
    const spans = button.getElementsByTagName('span');
    const costText = spans[1]?.textContent;
    const costMatch = costText && costText.match(/[\d.]+/); // Extract numerical part
    const costAmount = costMatch ? parseFloat(costMatch[0]).toFixed(2) : '0.00'; // Convert to number and format
    const formattedCost = `$${costAmount}`; // Format as a dollar amount

    if (typeof onButtonClick === 'function' && volumeValue) {
      const parsedVolume = parseFloat(volumeValue);
      if (!isNaN(parsedVolume)) {
        onButtonClick(parsedVolume);
      }
    }
    onAmountChange(increment);
    setCost(formattedCost);
  };

  const renderButtons = (requireEoi: boolean) => {
    const formatDollarAmount = (amount: string) => {
      const numericValue = parseFloat(amount);
      return `$${numericValue.toFixed(2)}`;
    };

    return increments
      .filter((inc) => Boolean(inc.requires_eoi) === requireEoi)
      .map((inc, index) => (
        <button
          key={index}
          onClick={handleButtonClick}
          data-volume={inc.volume}
        >
          <span>
            {getPremiumStyled({
              premium: String(inc.volume),
              valueOnly: RETURN_VALUE_ONLY,
            })}
          </span>
          {(!hideEmptyCost || Number(inc.subscriber_premium) > 0) && (
            <span className="lowercase">
              {getPremiumStyled({
                premium: String(inc.subscriber_premium),
                valueOnly: RETURN_VALUE_ONLY,
              })}{' '}
              {`${perPayText}`}
            </span>
          )}
        </button>
      ));
  };

  return (
    <div
      className={`collapse${show ? ' show' : ''}`}
      id="coverage-numbers"
      ref={numbersRef}
      // Make div focusable by adding tabindex - WCAG
      tabIndex={-1}
    >
      <div id="guaranteed" className="faux-select">
        {lastNonEoiVolume > 0 && <div className="title">{t('guaranteed')}</div>}
        {renderButtons(false)}
      </div>
      {lastNonEoiVolume !== maxValue && (
        <div id="approval" className="faux-select">
          <div className="title">{t('requires')}</div>
          {renderButtons(true)}
        </div>
      )}
    </div>
  );
};
